import axios from 'axios';
import React from 'react';

import Banner from 'plaid-threads/Banner';
import Button from 'plaid-threads/Button';
import IconButton from 'plaid-threads/IconButton';
import Toggle from 'plaid-threads/ToggleSwitch';
import { Copy, Refresh } from 'plaid-threads/Icons';
import Label from 'plaid-threads/Label';
import Note, { Props as NoteProps } from 'plaid-threads/Note';
import NumberInput from 'plaid-threads/NumberInput';
import LoadingSpinner from 'plaid-threads/LoadingSpinner';
import TextInput from 'plaid-threads/TextInput';

import { AppContext } from '../App';
import { Currency } from '../constants';
import { getAmountString } from '../currencyUtils';
import { Box } from 'plaid-threads';

type Consent = {
  consent_id: string;
  status: string;
  created_at: string;
  reference: string;
  type?: string;
  constraints: {
    valid_date_time: {
      from: string | null;
      to: string | null;
    };
    max_payment_amount: {
      currency: string;
      value: number;
    };
    periodic_amounts: Array<{
      amount: { currency: string; value: number };
      interval: string;
      alignment: string;
    }>;
  };
};

type Payment = {
  payment_id: string;
  status: string;
  updated_at: string;
  amount: {
    currency: string;
    value: number;
  };
  reference: string;
  adjusted_reference?: string;
};

interface ExecutePaymentProps {
  consentId: string | null;
}

const ExecutePayment: React.FC<ExecutePaymentProps> = (props) => {
  const [consentId, setConsentId] = React.useState<string>(
    props.consentId || '',
  );
  const [consent, setConsent] = React.useState<Consent | null>(null);
  const [isLoadingConsent, setIsLoadingConsent] =
    React.useState<boolean>(false);
  const [paymentAmount, setPaymentAmount] = React.useState<string>('0');
  const [paymentError, setPaymentError] = React.useState<string>('');
  const [payments, setPayments] = React.useState<Payment[]>([]);
  const [isExecutingPayment, setIsExecutingPayment] =
    React.useState<boolean>(false);
  const [useCustomReference, setUseCustomReference] =
    React.useState<boolean>(false);
  const { currency } = React.useContext(AppContext);

  React.useEffect(() => {
    setConsentId(props.consentId || '');
  }, [props.consentId]);

  React.useEffect(() => {
    if (!consentId) {
      setPaymentError('Consent ID cannot be empty');
      return;
    }

    setPaymentError('');
    setConsent(null);
    setPayments([]);
    setIsLoadingConsent(true);

    getConsent(consentId)
      .then(({ consent }) => {
        setConsent(consent);
        return getPayments(consent.consent_id).then(({ payments }) => {
          setPayments(payments);
        });
      })
      .catch((error) => {
        setPaymentError(error + '');
      })
      .then(() => {
        setIsLoadingConsent(false);
      });
  }, [consentId]);

  function onCopyConsentClick() {
    navigator.clipboard.writeText(consentId);
  }

  function onMakePaymentClick() {
    setPaymentError('');
    if (!consent?.consent_id) {
      // should never happen, "Make payment" is not rendered w/o consent
      return;
    }

    setIsExecutingPayment(true);
    setPayments([
      {
        payment_id: '',
        status: 'LOADING',
        updated_at: new Date().toISOString(),
        amount: {
          currency,
          value: +paymentAmount,
        },
        reference: '',
      },
      ...payments,
    ]);

    executeConsentPayment(+paymentAmount, consent, currency, useCustomReference)
      .then(({ paymentId }) => {
        return getPayment(paymentId).then(({ payment }) => {
          return [payment, ...payments];
        });
      })
      .catch((error) => {
        setPaymentError(error?.response?.data?.display_message || error + '');
        return payments;
      })
      .then((payments) => {
        setIsExecutingPayment(false);
        setPayments(payments.filter((payment) => payment.status !== 'LOADING'));
      });
  }

  function onPaymentRefresh(paymentId: string) {
    setPayments(
      payments.map((payment) => {
        return payment.payment_id !== paymentId
          ? payment
          : {
              ...payment,
              status: 'LOADING',
            };
      }),
    );

    getPayment(paymentId)
      .then(({ payment }) => {
        setPayments(
          payments.map((oldPayment) => {
            return oldPayment.payment_id === paymentId ? payment : oldPayment;
          }),
        );
      })
      .catch((error) => {
        setPaymentError(error + '');
      });
  }

  function onRevokeConsentClick() {
    if (!consent?.consent_id) {
      // should never happen, "Revoke" is not rendered w/o consent
      return;
    }

    setPaymentError('');
    setIsLoadingConsent(true);
    setIsExecutingPayment(true);

    revokeConsent(consent.consent_id)
      .then(({ consent }) => {
        setConsent(consent);
      })
      .catch((error) => {
        setPaymentError(error?.response?.data?.display_message || error + '');
      })
      .then(() => {
        setIsLoadingConsent(false);
        setIsExecutingPayment(false);
      });
  }

  function createBanner(
    paymentError: string,
    consentStatus: string | undefined,
  ) {
    let isError = false;
    let message = '';
    if (paymentError) {
      isError = true;
      message = paymentError;
    } else if (consentStatus === 'REVOKED') {
      isError = true;
      message = 'Consent has been revoked and payments will fail.';
    } else if (consentStatus === 'AUTHORISED') {
      message =
        "You're now ready to make instant payments, with no additional bank authentication required.";
    }
    if (!message) return null;
    return (
      <div style={{ marginBottom: '30px' }}>
        <Banner
          title={isError ? 'Error' : 'Success'}
          error={isError}
          success={!isError}
        >
          {message}
        </Banner>
      </div>
    );
  }

  return (
    <div className="Consents__Execute">
      {createBanner(paymentError, consent?.status)}
      {consent && (
        <>
          <div className="Consents__row">
            <NumberInput
              value={paymentAmount}
              id={'paymentAmount'}
              label={`Payment amount (max of ${getAmountString(5, currency)})`}
              onChange={(e) => setPaymentAmount(e.currentTarget.value)}
              className="Consents__Execute__PaymentAmount"
            />
            <Button
              wide
              animatedCaret
              onClick={onMakePaymentClick}
              disabled={isExecutingPayment}
            >
              Make payment
            </Button>
          </div>
          <div className="Consents__row">
            <Toggle
              id="qwe"
              value={useCustomReference}
              onChange={() => setUseCustomReference(!useCustomReference)}
              trueText=" "
              falseText=" "
            >
              generate custom reference
            </Toggle>
          </div>
        </>
      )}
      {payments.length > 0 && (
        <div>
          {payments.map((payment) => {
            return (
              <PaymentDetails
                payment={payment}
                onRefresh={onPaymentRefresh}
                key={payment.payment_id}
              />
            );
          })}
        </div>
      )}
      <Box bgColor={consent ? 'tertiary' : undefined}>
        <div className="Consents__row" style={{ marginTop: '30px' }}>
          <TextInput
            value={consentId}
            id="consentId"
            label="Consent ID"
            codeType
            onChange={(e) => setConsentId(e.target.value)}
            disabled={isLoadingConsent}
            iconButtons={[
              <IconButton
                accessibilityLabel="Copy consent ID"
                icon={<Copy />}
                onClick={onCopyConsentClick}
              />,
            ]}
          />
          {consent && (
            <Button inline centered onClick={onRevokeConsentClick}>
              Revoke
            </Button>
          )}
        </div>
        {isLoadingConsent && <LoadingSpinner />}
        {consent && (
          <>
            <div className="Consents__row">
              <ConsentDetails consent={consent} />
            </div>
          </>
        )}
      </Box>
    </div>
  );
};

// According to spec: The time element of the date should be disregarded in computing the date range
function formatConsentDateString(dateTime: string): string {
  return new Date(dateTime).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatCurrencyAmount(currency: string, value: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
  }).format(value);
}

interface ConsentDetailsProps {
  consent: Consent | null | undefined;
}

const ConsentDetails: React.FC<ConsentDetailsProps> = ({ consent }) => {
  if (!consent) {
    return null;
  }

  const {
    constraints: { valid_date_time, max_payment_amount, periodic_amounts },
  } = consent;

  const summary: React.ReactElement<any, any>[] = [];
  if (consent.type) {
    summary.push(
      <span>
        <Note small info>
          {consent.type === 'COMMERCIAL' ? 'Commercial VRP' : 'Sweeping VRP'}
        </Note>{' '}
      </span>,
    );
  }
  summary.push(<span>The consent </span>);
  if (consent.reference) {
    summary.push(<strong>{consent.reference} </strong>);
  }
  if (valid_date_time?.from || valid_date_time?.to) {
    summary.push(<span>valid </span>);
    if (valid_date_time.from) {
      summary.push(
        <span>
          from <strong>{formatConsentDateString(valid_date_time.from)}</strong>{' '}
        </span>,
      );
    }
    if (valid_date_time.to) {
      summary.push(
        <span>
          until <strong>{formatConsentDateString(valid_date_time.to)}</strong>{' '}
        </span>,
      );
    }
    summary.push(<span>and </span>);
  }
  summary.push(<span>can be used to execute payments with maximum </span>);
  summary.push(
    <strong>
      {formatCurrencyAmount(
        max_payment_amount.currency,
        max_payment_amount.value,
      )}{' '}
    </strong>,
  );
  summary.push(<span>amount per single transaction</span>);
  if (periodic_amounts.length > 0) {
    summary.push(<span> and:</span>);
    const periodicLimits = periodic_amounts.reduce<
      React.ReactElement<any, any>[]
    >((periodicLimits, periodicAmount, index) => {
      periodicLimits.push(
        <li key={index}>
          not more than{' '}
          <strong>
            {formatCurrencyAmount(
              periodicAmount.amount.currency,
              periodicAmount.amount.value,
            )}
          </strong>{' '}
          per <strong>{periodicAmount.alignment.toLocaleLowerCase()}</strong>{' '}
          <strong>{periodicAmount.interval.toLocaleLowerCase()}</strong>;
        </li>,
      );
      return periodicLimits;
    }, []);
    summary.push(<ul className="Consents__list">{periodicLimits}</ul>);
  } else {
    summary.push(<span>.</span>);
  }
  if (consent.status === 'AUTHORISED') {
    summary.push(
      <span>
        Current consent status is{' '}
        <Note small success>
          {consent.status}
        </Note>{' '}
        and it is ready for executing payments.
      </span>,
    );
  } else if (
    consent.status === 'REVOKED' ||
    consent.status === 'EXPIRED' ||
    consent.status === 'REJECTED'
  ) {
    summary.push(
      <span>
        Consent is{' '}
        <Note small error>
          {consent.status}
        </Note>
        , payments will fail.
      </span>,
    );
  } else if (consent.status === 'UNAUTHORISED') {
    summary.push(
      <span>
        Consent is{' '}
        <Note small error>
          {consent.status}
        </Note>
        . It should be authorised using Plaid Link, and then could be used to
        execute payments.
      </span>,
    );
  }

  return <div className="Consents__details">{summary}</div>;
};

function formatPaymentDateTimeString(dateTime: string): string {
  return new Date(dateTime).toLocaleString(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  });
}

function getStatusNoteProps(status: string): NoteProps {
  if (
    [
      'REJECTED',
      'UNKNOWN',
      'INPUT_NEEDED',
      'INSUFFICIENT_FUNDS',
      'FAILED',
      'BLOCKED',
      'CANCELLED',
    ].includes(status)
  ) {
    return { error: true };
  }
  if (['EXECUTED', 'INITIATED'].includes(status)) {
    return { success: true };
  }
  return { info: true };
}

interface PaymentDetailsProps {
  payment: Payment | null;
  onRefresh: (paymentId: string) => void;
}

const PaymentDetails: React.FC<PaymentDetailsProps> = ({
  payment,
  onRefresh,
}) => {
  if (!payment) {
    return null;
  }

  function onPaymentRefreshClick() {
    if (!payment) {
      return;
    }
    onRefresh(payment.payment_id);
  }

  return (
    <div className="Consents__Execute__PaymentRow">
      <Label className="Consents__Execute__PaymentRow__DateTime">
        {formatPaymentDateTimeString(payment.updated_at)}
      </Label>
      <div className="Consents__Execute__PaymentRow__Amount">
        {formatCurrencyAmount(payment.amount.currency, payment.amount.value)}
      </div>
      <div className="Consents__Execute__PaymentRow__Reference">
        <span>{payment.reference}</span>
        {payment.adjusted_reference && (
          <span className="Consents__Execute__PaymentRow__AdjustedReference">
            ({payment.adjusted_reference})
          </span>
        )}
      </div>
      <div className="Consents__Execute__PaymentRow__Tail">
        <Note solid {...getStatusNoteProps(payment.status)}>
          <div>{payment.status}</div>
        </Note>
        {payment.status === 'LOADING' ? (
          <Refresh className="Consents__Execute__PaymentRow__LoadingIcon" />
        ) : (
          <IconButton
            accessibilityLabel="Refresh"
            icon={<Refresh />}
            onClick={onPaymentRefreshClick}
          />
        )}
      </div>
    </div>
  );
};

function getConsent(consentId: string): Promise<{ consent: Consent }> {
  return axios
    .get(`${process.env.REACT_APP_SERVER_URL}/consent/${consentId}`, {
      withCredentials: true,
    })
    .then((response) => {
      return { consent: response.data.consent };
    });
}

function revokeConsent(consentId: string): Promise<{ consent: Consent }> {
  return axios
    .post(
      `${process.env.REACT_APP_SERVER_URL}/consent/revoke`,
      { consentId },
      { withCredentials: true },
    )
    .then((response) => {
      return { consent: response.data.consent };
    });
}

function executeConsentPayment(
  amount: number,
  consent: Consent,
  currency: Currency,
  useCustomReference: boolean,
): Promise<{ paymentId: string }> {
  return axios
    .post(
      `${process.env.REACT_APP_SERVER_URL}/consent/payment/execute`,
      { amount, currency, consentId: consent.consent_id, useCustomReference },
      { withCredentials: true },
    )
    .then((response) => {
      const {
        payment: { payment_id },
      } = response.data;

      if (!payment_id) {
        return Promise.reject('Failed to execute Payment from Consent');
      }

      return { paymentId: payment_id };
    });
}

function getPayment(paymentId: string): Promise<{ payment: Payment }> {
  return axios
    .get(`${process.env.REACT_APP_SERVER_URL}/payment/${paymentId}`, {
      withCredentials: true,
    })
    .then((response) => {
      const {
        data: { payment },
      } = response;
      return {
        payment: {
          payment_id: payment.payment_id,
          status: payment.status.substring('PAYMENT_STATUS_'.length),
          updated_at: payment.last_status_update,
          amount: payment.amount,
          reference: payment.reference,
          adjusted_reference: payment.adjusted_reference,
        },
      };
    });
}

function getPayments(consentId: string): Promise<{ payments: Array<Payment> }> {
  const url = new URL('/payment/list', process.env.REACT_APP_SERVER_URL);
  url.searchParams.set('consentId', consentId);
  url.searchParams.set('count', '10');

  return axios
    .get(url.toString(), { withCredentials: true })
    .then((response) => {
      return {
        payments: (response.data.payments || []).map((payment: any) => {
          return {
            payment_id: payment.payment_id,
            status: payment.status.substring('PAYMENT_STATUS_'.length),
            updated_at: payment.last_status_update,
            amount: payment.amount,
            reference: payment.reference,
            adjusted_reference: payment.adjusted_reference,
          };
        }),
      };
    });
}

export default ExecutePayment;
